import React, { useState } from 'react'
import DashboardBackground from '../../components/ReUsableComponents/DashboardBackground';
import SideBarV1 from '../NewDashboard/SideBarV1';
import HeaderV1 from '../NewDashboard/HeaderV1';
import AuthButton from '../LogInPage/LoginButton';
import { downloadBlobFile, formatDate } from '../../utils';
import { BiDownload, BiX } from 'react-icons/bi';
import { useGetPurchaseHistoryQuery, useLazyDownloadInvoiceQuery } from '../../redux/API/Payments/paymentsapi';
import { getItem } from '../../utils/token';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/Store/store';
import FullMarksLoader from '../../components/ReUsableComponents/FullMarksLoader';
import { toast } from 'react-toastify';

export type PlanColor = {
  planColor: string;
  boxShadow: string;
}

export type PlanPurchase = {
  dateOfPurchase: string;
  planName: string;
  planId: string;
  invoiceId: string;
  isActive: boolean;
  planEndDate: string;
  transactionId: string;
  planColor: PlanColor;
}


const PurchaseHistory = () => {

  const {parentStudentId,startedTestId} = useSelector((state : RootState) => state.root.startFullTest);
  const [downloadingIds, setDownloadingIds] = useState<{ [key: string]: boolean }>({});

  const {data:purchaseHistory,isSuccess,isLoading,isError} = useGetPurchaseHistoryQuery({userId :getItem('userRole') === 'STUDENT' ? getItem('userId') : parentStudentId});
  const [downloadInvoice] = useLazyDownloadInvoiceQuery();

  const handleDownloadInvoice = async (transactionId : string) => {
    if (downloadingIds[transactionId]) return;

    try{
      setDownloadingIds((prev) => ({ ...prev, [transactionId]: true }));
      const userId = getItem('userRole') ===  'STUDENT' ? getItem('userId') : parentStudentId;
      const response = await downloadInvoice({transactionId,userId});
      let fileName = `invoice_${transactionId}.pdf`;
      downloadBlobFile(response?.data,fileName);
    }catch(err){
      toast(
          ({ closeToast }) => (
            <div className="flex items-center justify-between gap-3 px-3 py-2 font-gully border-2 border-satPrepRed rounded-md bg-white w-full" style={{ boxShadow : '0px 4px 8px 0px rgba(28, 37, 44, 0.08)'}}>
                <p className='font-gully text-satPrepBlack text-lg'>Failed to download. Please retry.</p>
                <button onClick={closeToast} className="text-xl hover:text-gray-500">
                  <BiX />
                </button>
            </div>
          ),
          {
              closeButton: false,
              hideProgressBar: true,
              autoClose: 3000,
              position: "top-right",
              className: 'max-w-[400px] w-full mx-4',
              style: {
                padding: 0,
                background: 'transparent',
                boxShadow: 'none'
              }
          }
      );
      console.error(err);
    }finally {
      setDownloadingIds((prev) => ({ ...prev, [transactionId]: false }));
    }
  }

  const isFirstTest = getItem('userRole') === 'STUDENT' && startedTestId.isFirstTest;

  return (
    <DashboardBackground>
      <div className={`p-5 flex gap-[1%] w-full ${isFirstTest ? 'flex-col' : 'flex-row'}`}>
        {/* Sidebar */}
        <SideBarV1 isFirstTest={isFirstTest} />

        <section
          className={`flex ${!isFirstTest && 'flex-col'} justify-center items-start gap-[2vh]`}
          style={{ width: isFirstTest ? '100%' : "calc(100vw - 240px)", height: isFirstTest ? '' : "calc(100vh - 40px)" }}
        >
          {!isFirstTest && <HeaderV1/>}

          <div
            className="px-3 py-2 w-full flex flex-grow justify-between items-center self-stretch rounded-[28px] border-[0.5px]  border-satPrepGrey bg-satPrepTransWhite shadow-widget-card backdrop-blur-sevenFive relative"
            style={{ height: "calc(100vh - 12vh - 40px)" }}
          >
            <div className="h-full py-1 w-full overflow-y-auto scroll4 flex flex-col gap-9 relative">
                <h2 className='text-center text-satPrepAccent text-[56px] font-bold py-3'>Purchase History</h2>
                <div className='flex flex-col gap-[10px]'>
                {isLoading && <div className='absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2'><FullMarksLoader width={200} height={200}/></div>}
                
                {isSuccess && purchaseHistory?.data?.length === 0 ? (
                  <div className="flex items-center justify-center w-full">
                    <p className="text-3xl text-satPrepBlack font-gully font-bold text-center">
                      You haven’t made any purchases yet.
                    </p>
                  </div>
                ) : (
                  purchaseHistory?.data?.map((purchase: PlanPurchase) => (
                    <div className="flex p-6 font-gully justify-between items-center bg-satPrepTransWhite shadow-widget-card backdrop-blur-sevenFive rounded-[28px]">
                      <div>
                        <p
                          className="text-[26px] font-semibold mb-2"
                          style={{ color: purchase?.planColor?.planColor }}
                        >
                          {purchase.planName}
                        </p>
                        <p className="text-satPrepBlack font-medium">
                          Date of Purchase:{" "}
                          {formatDate(purchase?.dateOfPurchase)}
                        </p>
                      </div>
                      <AuthButton
                        text="Download Invoice"
                        icon={<BiDownload size={22} />}
                        iconPosition="left"
                        onClick={() => {
                          handleDownloadInvoice(purchase?.transactionId);
                        }}
                        loader={!!downloadingIds[purchase.transactionId]}
                      />
                    </div>
                  ))
                )}

                {isError && (
                  <div className='flex items-center justify-center w-full'>
                    <p className='text-3xl text-satPrepBlack font-gully font-bold text-center'>Unable to fetch purchase history</p>
                  </div>)
                }
                </div>
            </div>
          </div>
        </section>
      </div>
    </DashboardBackground>
  );
}

export default PurchaseHistory